import { openDB, type IDBPDatabase } from 'idb';

/**
 * Browser persistence.
 *
 * IndexedDB, not localStorage: a payroll period with 200 employee calculations,
 * a year of attendance records and an audit log outgrow the ~5MB localStorage
 * quota quickly, and localStorage is synchronous — a 3MB write blocks the main
 * thread and the interface stutters exactly when the dataset gets interesting.
 *
 * localStorage is still used, deliberately, for *small preferences* that must be
 * readable before the first paint (the collapsed sidebar, the active demo
 * session) so the shell does not flash into the wrong layout.
 *
 * The mobile app has its own store. This one never touches it: `mock-services`
 * is the mobile app's live database and its schema is not ours to migrate.
 */

const DB_NAME = 'micarahr-hr-web';
const STORE_NAME = 'collections';

/** Bump when a collection's shape changes incompatibly. Triggers a re-seed. */
export const SCHEMA_VERSION = 1;

export interface KeyValueStore {
  get<T>(key: string): Promise<T | null>;
  set<T>(key: string, value: T): Promise<void>;
  remove(key: string): Promise<void>;
  keys(): Promise<string[]>;
  clear(): Promise<void>;
}

let dbPromise: Promise<IDBPDatabase> | null = null;

function database(): Promise<IDBPDatabase> {
  dbPromise ??= openDB(DB_NAME, 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    },
  });
  return dbPromise;
}

export function createIndexedDbStore(): KeyValueStore {
  return {
    async get<T>(key: string): Promise<T | null> {
      const value = await (await database()).get(STORE_NAME, key);
      return (value as T | undefined) ?? null;
    },
    async set<T>(key: string, value: T): Promise<void> {
      await (await database()).put(STORE_NAME, value, key);
    },
    async remove(key: string): Promise<void> {
      await (await database()).delete(STORE_NAME, key);
    },
    async keys(): Promise<string[]> {
      return (await (await database()).getAllKeys(STORE_NAME)) as string[];
    },
    async clear(): Promise<void> {
      await (await database()).clear(STORE_NAME);
    },
  };
}

/** In-memory store. Used by tests and as the fallback when IndexedDB is unavailable. */
export function createMemoryStore(seed: Record<string, unknown> = {}): KeyValueStore {
  const map = new Map<string, unknown>(Object.entries(seed));
  return {
    async get<T>(key: string) {
      return (map.get(key) as T | undefined) ?? null;
    },
    async set<T>(key: string, value: T) {
      map.set(key, value);
    },
    async remove(key: string) {
      map.delete(key);
    },
    async keys() {
      return [...map.keys()];
    },
    async clear() {
      map.clear();
    },
  };
}

/* -------------------------------------------------------------------------- */
/* Small preferences                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Synchronous preference storage.
 *
 * Only for values the first render depends on. Everything else belongs in
 * IndexedDB.
 */
export const preferences = {
  read<T>(key: string, fallback: T): T {
    if (typeof window === 'undefined') return fallback;
    try {
      const raw = window.localStorage.getItem(`micarahr.${key}`);
      return raw === null ? fallback : (JSON.parse(raw) as T);
    } catch {
      return fallback;
    }
  },
  write<T>(key: string, value: T): void {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(`micarahr.${key}`, JSON.stringify(value));
    } catch {
      // A full or disabled localStorage must never break the application; the
      // preference simply does not survive a reload.
    }
  },
  remove(key: string): void {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.removeItem(`micarahr.${key}`);
    } catch {
      // Ignored for the same reason.
    }
  },
};
